import type { ElementType, Ref } from 'react'
import React, { useCallback, forwardRef } from 'react'
import type { UtilityClassNames } from '@s4l/utility-css'
import { cn } from '@s4l/utility-css'
import debug from 'debug'
import Typography from '../Typography'
import styles from './index.module.css'

const log = debug('s4l:ds:Button')

export type ButtonSize = 'large' | 'medium' | 'small' | 'extraSmall'
type ButtonVariant =
  | 'filled'
  | 'filled-rounded'
  | 'outlined'
  | 'outlined-rounded'
  | 'text'
  | 'text-rounded'
  | 'text-flat'
  | 'elevated'
  | 'elevated-rounded'
  | 'icon'
  | 'icon-rounded'
  | 'icon-flat'

interface IconProps {
  size?: number
  color?: string
  className?: string
}

type ButtonOwnProps<E extends ElementType = 'button'> = {
  className?: UtilityClassNames
  customColor?: string
  children?: React.ReactNode
  leadingIcon?: React.ReactElement<IconProps>
  trailingIcon?: React.ReactElement<IconProps>
  size?: ButtonSize
  variant?: ButtonVariant
  hover?: boolean
  active?: boolean
  focus?: boolean
  disabled?: boolean
  fullWidth?: boolean
  href?: string
  target?: '_blank' | '_self' | '_parent' | '_top' | 'framename'
  as?: E
}

export type ButtonProps<E extends ElementType = 'button'> = ButtonOwnProps<E> &
  Omit<React.ComponentPropsWithoutRef<E>, keyof ButtonOwnProps>

function ButtonComponent<E extends ElementType = 'button'>(
  {
    className,
    customColor,
    children,
    leadingIcon,
    trailingIcon,
    size = 'large',
    variant = 'filled',
    hover,
    active,
    focus,
    disabled,
    as,
    fullWidth,
    target = '_self',
    ...props
  }: ButtonProps<E>,
  ref: Ref<HTMLButtonElement | HTMLAnchorElement | HTMLElement>
) {
  const innerOnClick = useCallback(
    (evt: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      if (disabled) {
        evt.preventDefault()
        return
      }
      props.onClick?.(evt)
    },
    [disabled, props]
  )

  if (
    (variant === 'icon' || variant === 'icon-rounded' || variant === 'icon-flat') &&
    !leadingIcon
  ) {
    log('Button with variant "icon" must have a leadingIcon')
    return null
  }

  if (
    customColor &&
    variant !== 'filled' &&
    variant !== 'filled-rounded' &&
    variant !== 'text-flat'
  ) {
    log(
      'Button with variant "customColor" must have a variant "filled", "filled-rounded" or "text-flat"'
    )
    return null
  }

  const Component = as || ('button' as ElementType)

  if (Component === 'a' && !props.href) {
    log('Button with as="a" must have a href')
    return null
  }

  const state = [
    hover && 'hover',
    active && 'active',
    focus && 'focus',
    disabled && 'disabled',
    fullWidth && 'fullWidth',
  ]
    .filter(Boolean)
    .join(' ')

  const typographySize = size === 'large' ? 'l' : 'm'

  const iconSize = {
    large: {
      filled: 24,
      'filled-rounded': 24,
      outlined: 24,
      'outlined-rounded': 24,
      text: 24,
      'text-rounded': 24,
      'text-flat': 24,
      elevated: 24,
      'elevated-rounded': 24,
      icon: 24,
      'icon-rounded': 24,
      'icon-flat': 24,
    },

    medium: {
      filled: 16,
      'filled-rounded': 16,
      outlined: 16,
      'outlined-rounded': 16,
      text: 16,
      'text-rounded': 16,
      'text-flat': 16,
      elevated: 16,
      'elevated-rounded': 16,
      icon: 24,
      'icon-rounded': 24,
      'icon-flat': 24,
    },

    small: {
      filled: 16,
      'filled-rounded': 16,
      outlined: 16,
      'outlined-rounded': 16,
      text: 16,
      'text-rounded': 16,
      'text-flat': 16,
      elevated: 16,
      'elevated-rounded': 16,
      icon: 24,
      'icon-rounded': 24,
      'icon-flat': 24,
    },

    extraSmall: {
      filled: 16,
      'filled-rounded': 16,
      outlined: 16,
      'outlined-rounded': 16,
      text: 16,
      'text-rounded': 16,
      'text-flat': 16,
      elevated: 16,
      'elevated-rounded': 16,
      icon: 24,
      'icon-rounded': 24,
      'icon-flat': 16,
    },
  }[size][variant]

  const clonedLeadingIcon = leadingIcon ? React.cloneElement(leadingIcon, { size: iconSize }) : null
  const clonedTrailingIcon = trailingIcon
    ? React.cloneElement(trailingIcon, { size: iconSize })
    : null

  const commonProps = {
    disabled: Component === 'button' ? disabled : undefined,
    'aria-disabled': Component === 'a' && disabled ? true : undefined,
    className: cn(
      styles.button,
      styles[size],
      styles[variant],
      customColor && styles.customColor,
      className
    ),
    'data-button-state': state,
    ...(customColor && {
      style: {
        ['--button-custom-color' as keyof React.CSSProperties]: customColor,
      } as React.CSSProperties,
    }),
    ...props,
    onClick: innerOnClick,
  }

  const buttonProps = {
    ...commonProps,
    disabled,
    rel: props.rel,
    ref: ref as Ref<HTMLButtonElement>,
  }

  const anchorProps = {
    ...commonProps,
    'aria-disabled': disabled ? true : undefined,
    href: props.href,
    target,
    rel: target === '_blank' ? 'noopener noreferrer' : props.rel,
    ref: ref as Ref<HTMLAnchorElement>,
  }

  const isIconButton = variant === 'icon' || variant === 'icon-rounded' || variant === 'icon-flat'

  const content = (
    <>
      {clonedLeadingIcon}
      {!isIconButton && (
        <Typography variant="body" size={typographySize} bold ellipsis>
          {children}
        </Typography>
      )}
      {!isIconButton && clonedTrailingIcon}
    </>
  )

  if (as === 'a') {
    return <a {...anchorProps}>{content}</a>
  }

  return (
    <button type="button" {...buttonProps}>
      {content}
    </button>
  )
}

const Button = forwardRef(ButtonComponent) as <E extends ElementType = 'button'>(
  props: ButtonProps<E> & {
    ref?: Ref<
      E extends 'button' ? HTMLButtonElement : E extends 'a' ? HTMLAnchorElement : HTMLElement
    >
  }
) => ReturnType<typeof ButtonComponent>

export default Button
